import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  UploadCloud, Download, RefreshCw, ShieldCheck, ShieldAlert, CheckCircle2,
  AlertTriangle, Rocket, FileJson, FileSignature, GitBranch,
} from "lucide-react";

/**
 * Agreement Template Deployment — preview → production one-way pipeline.
 *
 * Same architecture and shared secret as Content Sync, but for the
 * `agreement_templates` collection + DOCX bytes. See
 * /app/backend/agreements/template_sync.py.
 */
export default function AdminTemplateDeploy() {
  const [config, setConfig] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);   // parsed JSON of uploaded snapshot
  const [diff, setDiff] = useState(null);           // dry-run response
  const [applying, setApplying] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m, h] = await Promise.all([
        api.get("/admin/template-sync/config"),
        api.get("/admin/template-sync/manifest"),
        api.get("/admin/template-sync/history"),
      ]);
      setConfig(c.data);
      setManifest(m.data);
      setHistory(h.data.history || []);
    } catch (e) { toast.error("Failed to load: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const download = async () => {
    try {
      const r = await api.get("/admin/template-sync/download-snapshot");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `template-snapshot-${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Snapshot downloaded — ${r.data.template_count} templates`);
    } catch (e) { toast.error("Download failed: " + (e.response?.data?.detail || e.message)); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      setSnapshot(parsed);
      setDiff(null);
      toast.success(`Loaded snapshot with ${parsed.template_count} templates`);
    } catch (err) { toast.error("Not a valid JSON snapshot: " + err.message); }
  };

  const runDryRun = async () => {
    if (!snapshot) return;
    try {
      const r = await api.post("/admin/template-sync/import?dry_run=true", snapshot);
      setDiff(r.data);
    } catch (e) {
      toast.error("Preview failed: " + (e.response?.data?.detail || e.message));
      setDiff(null);
    }
  };

  const apply = async () => {
    if (!snapshot) return;
    if (!window.confirm(
      `Apply this snapshot?\n\nInserts: ${diff?.totals.new || 0}\nUpdates: ${diff?.totals.updated || 0}\nDocx-only: ${diff?.totals.docx_only || 0}\nUnchanged: ${diff?.totals.unchanged || 0}\n\nDeployment is one-way and history is preserved.`
    )) return;
    setApplying(true);
    try {
      const r = await api.post("/admin/template-sync/import?dry_run=false", snapshot);
      toast.success(`Deployed. Report saved (${r.data.sync_id}).`);
      setSnapshot(null); setDiff(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error("Deploy failed: " + (e.response?.data?.detail || e.message)); }
    finally { setApplying(false); }
  };

  const secretOk = config?.secret_configured;

  return (
    <div className="p-6" data-testid="template-deploy">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A2540] flex items-center gap-2">
            <Rocket className="w-6 h-6" /> Deploy Templates
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            One-way promotion of agreement templates from Preview to Production. HMAC-signed snapshot flow — no direct network path between environments.
          </p>
        </div>
        <button onClick={load} data-testid="td-refresh" className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm inline-flex items-center gap-2 hover:bg-[#F9FAFB]">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      {!secretOk && !loading && (
        <div className="mb-6 border border-red-200 bg-red-50 rounded-md p-4 text-sm text-red-800 inline-flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">CONTENT_SYNC_SECRET is not configured on this environment.</div>
            <div className="text-xs mt-1">Set the same secret on preview and production (never commit it). Until then, neither snapshot download nor import will work.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* LEFT — Preview: build + download snapshot */}
        <section className="border border-[#E5E7EB] rounded-lg bg-white">
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-[#0A2540]">1. Build snapshot (preview)</h2>
          </div>
          <div className="p-5 space-y-3">
            <div className="text-xs text-[#6B7280]">
              Includes: DOCX bytes, field configuration, checksum, discovered fields, required fields, status,
              and preprocessing aliases per template.
            </div>
            {manifest && (
              <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr><th className="text-left px-3 py-1.5">Code</th><th className="text-left px-3 py-1.5">Version</th><th className="text-left px-3 py-1.5">Status</th><th className="text-left px-3 py-1.5">Size</th><th className="text-left px-3 py-1.5">sha256</th></tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {manifest.templates.map(t => (
                      <tr key={`${t.code}-${t.version}`} data-testid={`td-tpl-${t.code}-${t.version}`}>
                        <td className="px-3 py-1.5">{t.code}</td>
                        <td className="px-3 py-1.5">v{t.version}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            t.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"
                          }`}>{t.status}</span>
                        </td>
                        <td className="px-3 py-1.5 tabular-nums">{Math.round((t.docx_size_bytes || 0) / 1024)} KB</td>
                        <td className="px-3 py-1.5 font-mono text-[10px]">{(t.docx_b64_sha256 || "").slice(0, 12)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              onClick={download}
              disabled={!secretOk || !manifest?.count}
              data-testid="td-download"
              className="w-full h-10 rounded-md bg-[#0066FF] text-white text-sm inline-flex items-center justify-center gap-2 hover:bg-[#0052CC] disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> Download signed snapshot ({manifest?.count || 0})
            </button>
            <div className="text-[11px] text-[#6B7280]">
              Snapshot is HMAC-SHA256 signed and expires after {config?.snapshot_max_age_hours || 48}h.
            </div>
          </div>
        </section>

        {/* RIGHT — Production: upload + preview + apply */}
        <section className="border border-[#E5E7EB] rounded-lg bg-white">
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
            <UploadCloud className="w-4 h-4 text-emerald-600" />
            <h2 className="text-sm font-semibold text-[#0A2540]">2. Upload &amp; apply (production)</h2>
          </div>
          <div className="p-5 space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              onChange={onFile}
              data-testid="td-file"
              className="block text-xs w-full"
            />
            {snapshot && (
              <div className="border border-[#E5E7EB] rounded-md p-3 text-xs bg-[#F9FAFB]">
                <div className="flex items-center gap-2 mb-1">
                  <FileJson className="w-3.5 h-3.5 text-blue-600" />
                  <span className="font-medium">Loaded snapshot</span>
                </div>
                <div className="text-[#6B7280]">Generated {String(snapshot.generated_at).slice(0, 19).replace("T", " ")} by {snapshot.generated_by}</div>
                <div className="text-[#6B7280]">{snapshot.template_count} templates · signature {String(snapshot.signature).slice(0, 12)}…</div>
                <button
                  onClick={runDryRun}
                  data-testid="td-dryrun"
                  className="mt-2 h-8 px-3 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs inline-flex items-center gap-1.5 hover:bg-blue-100"
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> Preview diff
                </button>
              </div>
            )}
            {diff && (
              <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
                <div className="grid grid-cols-4 divide-x divide-[#E5E7EB]">
                  <SumCell label="Insert" value={diff.totals.new} tone="emerald" />
                  <SumCell label="Update" value={diff.totals.updated} tone="blue" />
                  <SumCell label="DOCX only" value={diff.totals.docx_only} tone="amber" />
                  <SumCell label="Unchanged" value={diff.totals.unchanged} tone="slate" />
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr><th className="text-left px-3 py-1.5">Template</th><th className="text-left px-3 py-1.5">Action</th><th className="text-left px-3 py-1.5">Fields</th><th className="text-left px-3 py-1.5">sha256 change</th></tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]" data-testid="td-diff-rows">
                    {diff.templates.map(r => (
                      <tr key={`${r.code}-${r.version}`}>
                        <td className="px-3 py-1.5">{r.code} v{r.version}</td>
                        <td className="px-3 py-1.5">
                          <ActionPill action={r.action} />
                        </td>
                        <td className="px-3 py-1.5">{r.remote_field_count}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px]">
                          {(r.previous_docx_sha256 || "—").toString().slice(0, 8)}…&nbsp;→&nbsp;{(r.remote_docx_sha256 || "").slice(0, 8)}…
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-3 border-t border-[#E5E7EB]">
                  <button
                    onClick={apply}
                    disabled={applying || !secretOk}
                    data-testid="td-apply"
                    className="w-full h-10 rounded-md bg-emerald-600 text-white text-sm inline-flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Rocket className="w-4 h-4" /> {applying ? "Deploying…" : "Apply to production"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* HISTORY */}
      <section className="mt-8 border border-[#E5E7EB] rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-[#E5E7EB]">
          <h2 className="text-sm font-semibold text-[#0A2540]">Deployment history</h2>
        </div>
        <div className="overflow-x-auto">
          {history.length === 0 ? (
            <div className="p-5 text-sm text-[#6B7280]">No deployments yet on this environment.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] text-xs text-[#6B7280] uppercase">
                <tr><th className="text-left px-4 py-2">Sync ID</th><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">By</th><th className="text-left px-4 py-2">Inserts</th><th className="text-left px-4 py-2">Updates</th><th className="text-left px-4 py-2">Unchanged</th><th className="text-left px-4 py-2">Errors</th></tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {history.map(h => (
                  <tr key={h.sync_id}>
                    <td className="px-4 py-2 font-mono text-[10px]">{h.sync_id}</td>
                    <td className="px-4 py-2 text-xs">{String(h.started_at).slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-2 text-xs">{h.actor_email}</td>
                    <td className="px-4 py-2 tabular-nums text-emerald-700">{h.totals.insert}</td>
                    <td className="px-4 py-2 tabular-nums text-blue-700">{h.totals.update}</td>
                    <td className="px-4 py-2 tabular-nums text-slate-600">{h.totals.unchanged}</td>
                    <td className={`px-4 py-2 tabular-nums ${h.totals.error ? "text-red-700 font-medium" : "text-slate-500"}`}>{h.totals.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function SumCell({ label, value, tone }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-800",
    blue: "bg-blue-50 text-blue-800",
    amber: "bg-amber-50 text-amber-800",
    slate: "bg-slate-50 text-slate-700",
  };
  return (
    <div className={`p-3 text-center ${map[tone] || map.slate}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}

function ActionPill({ action }) {
  const map = {
    insert:            { c: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "insert" },
    update:            { c: "bg-blue-50 text-blue-800 border-blue-200",           label: "update" },
    docx_only_update:  { c: "bg-amber-50 text-amber-800 border-amber-200",         label: "docx only" },
    unchanged:         { c: "bg-slate-50 text-slate-700 border-slate-200",         label: "unchanged" },
  };
  const s = map[action] || map.unchanged;
  return <span className={`inline-flex text-[10px] px-2 py-0.5 rounded border ${s.c}`}>{s.label}</span>;
}
